import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { VideoSection } from '../../../types';
import HashtagHeader from './HashtagHeader';
import TextSlider from './TextSlider';
import RegisterButtonInForm from '../RegisterButtonInForm';
import RegisterFormModal from '../RegisterFormModal';
import OnlineCoursesHeader from './OnlineCoursesHeader';
import RegisterForm from '../RegisterForm';


/**
 * Video Header Component - Hero Section
 * Displays video background with text slider, CTAs, and form
 */
interface VideoHeaderProps {
  data?: VideoSection & {
    hashtags?: Array<{ id: string; hashtag: string; url: string }>;
    slogans?: Array<{ id: string; title: string; subtitle: string; description: string }>;
    videoFile?: { url: string; poster: string };
  };
}

// Fallback data when API response is null or invalid
const DEFAULT_VIDEO_HEADER_DATA = {
  title: 'Chinh Phục IELTS',
  subtitle: 'Khóa học IELTS toàn diện được thiết kế cho sự thành công',
  backgroundImage: '/hero-bg.jpg',
  videoUrl: '/hero-video.mp4',
  ctaText: 'Đăng Ký Ngay',
  ctaLink: '/register',
  hashtags: [
    { id: '1', hashtag: '#IELTSPreparation', url: '/courses' },
    { id: '2', hashtag: '#EnglishCourses', url: '/courses' },
    { id: '3', hashtag: '#OnlineLearning', url: '/courses' },
  ],
  slogans: [
    {
      id: '1',
      title: 'Chinh Phục IELTS',
      subtitle: 'Chuẩn Bị IELTS',
      description: 'Khóa học IELTS toàn diện được thiết kế cho sự thành công',
    },
    {
      id: '2',
      title: 'Phát Triển Tiếng Anh',
      subtitle: 'Tiếng Anh Giao Tiếp',
      description: 'Nâng cao kỹ năng giao tiếp tiếng Anh của bạn',
    },
    {
      id: '3',
      title: 'Đạt Mục Tiêu',
      subtitle: 'Học Viên Thành Công',
      description: 'Hàng ngàn học viên đã đạt được mục tiêu của họ',
    },
  ],
};

/**
 * Extracts a YouTube video ID from various YouTube URL formats.
 * Handles: watch?v=, youtu.be/, /embed/
 */
function getYouTubeId(url: string): string | null {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return match ? match[1] : null;
}

const VideoBackground = React.memo(({ videoUrl }: any) => {
  const youtubeId = videoUrl ? getYouTubeId(videoUrl) : null;

  return (
    <div className="relative w-full h-full bg-emerald-950 opacity-100">
      {youtubeId ? (
        // YouTube embed — autoplay + mute required for background use
        <iframe
          className="w-full h-full object-cover"
          src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1&loop=1&playlist=${youtubeId}&controls=0&showinfo=0&rel=0&modestbranding=1`}
          allow="autoplay; encrypted-media"
          allowFullScreen={false}
          style={{ border: 'none', pointerEvents: 'none' }}
          title="background-video"
        />
      ) : videoUrl &&
      <video
        src={videoUrl}
        className="w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
      />}
      <div className="absolute inset-0 bg-black/50"></div>
    </div>
  );
});
VideoBackground.displayName = 'VideoBackground';

const VideoHeader: React.FC<VideoHeaderProps> = ({ data }) => {
  const [isModalOpenRegister, setIsModalOpenRegister] = useState(false);

  // Use fallback if data is null or undefined
  const videoData = (data || DEFAULT_VIDEO_HEADER_DATA) as typeof DEFAULT_VIDEO_HEADER_DATA;

  const videoUrl = videoData.videoUrl || '';
  const slogans = videoData.slogans || DEFAULT_VIDEO_HEADER_DATA.slogans;
  const hashtags = videoData.hashtags || DEFAULT_VIDEO_HEADER_DATA.hashtags;

  return (
    <div className="relative w-full h-full lg:h-[calc(100vh-64px)]">
      {/* Video background with overlay — explicit z-0 so hashtag/content layers (z-30, z-20) always win */}
      <div className="absolute inset-0 z-0">
        <VideoBackground videoUrl={videoUrl} />
      </div>

      {/* Hashtag header - visible only on medium screens and up */}
      <div className="absolute top-0 left-0 right-0 z-30 hidden sm:block">
        <HashtagHeader data={hashtags} />
      </div>

      {/* Content container - responsive layout */}
      <div className="absolute inset-0 flex flex-col md:flex-row items-center justify-center z-20 p-4 md:p-6 lg:p-20">
        <div className="w-full text-center flex md:w-full lg:w-1/2 lg:text-left lg:pr-3 lg:justify-start h-full">
          <div className="relative w-[320px] mx-auto lg:w-[576px] lg:ml-auto text-white flex flex-col h-full">
            <div className="flex-1 flex flex-col justify-center">
              {/* Text Slider */}
              <TextSlider data={slogans} />

              {/* Register button shown on small to medium screens only */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mt-6 flex justify-center lg:hidden"
              >
                <RegisterButtonInForm onClick={() => setIsModalOpenRegister(true)} />
              </motion.div>

              <RegisterFormModal
                isOpen={isModalOpenRegister}
                onClose={() => setIsModalOpenRegister(false)}
                initText=""
              />
            </div>

            {/* Online courses header shown on large screens and up */}
            <div className="hidden lg:block">
              <OnlineCoursesHeader />
            </div>
          </div>
        </div>

        {/* Register Form shown on large screens */}
        <div className="hidden lg:flex lg:w-1/2 items-center justify-center">
          <motion.div
            initial={{ opacity: 0, x: 20, scale: 0.85 }}
            whileInView={{ opacity: 1, x: 0, scale: 0.85 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-full max-w-xl"
            style={{
              transformOrigin: 'center',
              transform: 'scale(0.75)'
            }}
          >
            <RegisterForm initText="" />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(VideoHeader);
